import { Core } from "@faire/design-tokens";
import { Heart } from "@faire/slate/icons/Heart";
import { cn } from "@faire/web/tools/cn";
import * as React from "react";
import styled from "styled-components";

interface IProps {
  favorited?: boolean;
  light?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  size?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

export const FavoriteButton: React.FC<IProps> = ({
  light,
  onClick,
  size,
  favorited,
  ariaLabel,
  disabled = false,
}) => {
  const Icon = light ? LightHeartIcon : DarkHeartIcon;

  return (
    <Button
      onClick={onClick}
      $size={size}
      $favorited={favorited}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      <Icon
        $favorited={favorited}
        className={cn(light ? "light-heart-icon" : "dark-heart-icon")}
      />
    </Button>
  );
};

const DarkHeartIcon = styled(Heart)<{ $favorited?: boolean }>`
  stroke-width: 1px;

  path {
    stroke: ${Core.icon.inverse};
    fill: ${Core.icon.default};
    fill-opacity: ${(props) => (props.$favorited ? 1 : 0.3)};
  }
`;

const LightHeartIcon = styled(Heart)<{ $favorited?: boolean }>`
  stroke-width: 1px;
  path {
    stroke: ${Core.icon.default};
    fill: ${(props) => (props.$favorited ? Core.icon.default : "transparent")};
  }
`;

const Button = styled.button<{
  $size?: string;
  $favorited?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${(props) => props.$size ?? "24px"};
  margin: 4px;

  transition: all 50ms ease-in;

  &:enabled:active {
    transform: scale(1.2);
  }

  &:focus-visible {
    outline: auto; /* Support Firefox & non-webkit browsers */
    outline: 2px solid -webkit-focus-ring-color;
    outline-offset: 2px;
  }

  &:enabled:hover {
    .dark-heart-icon {
      path {
        fill-opacity: ${(props) => (props.$favorited ? 1 : 0.8)};
      }
    }

    .light-heart-icon {
      path {
        stroke: ${Core.icon.critical};
      }
    }
  }
`;
